import React from "react";

const RFQ_WORKSPACE_PATH = "/rfqs/workspace/my-workspace";

const formatCurrency = (value = 0) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount === 0) return "Rs. 0";

  const absoluteAmount = Math.abs(amount);
  const formatter = new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: absoluteAmount >= 100000 ? 2 : 0,
  });

  if (absoluteAmount >= 10000000) return `${amount < 0 ? "-" : ""}Rs. ${formatter.format(absoluteAmount / 10000000)} Cr`;
  if (absoluteAmount >= 100000) return `${amount < 0 ? "-" : ""}Rs. ${formatter.format(absoluteAmount / 100000)} L`;
  return `${amount < 0 ? "-" : ""}Rs. ${formatter.format(absoluteAmount)}`;
};

const buildProjectWorkspaceUrl = ({ projectId, rfqType }) => {
  const params = new URLSearchParams({ status: "ALL" });
  if (projectId) params.set("projectId", projectId);
  if (rfqType) params.set("rfqType", rfqType);
  return `${RFQ_WORKSPACE_PATH}?${params.toString()}`;
};

const CountLink = ({ value = 0, projectId, rfqType, onNavigate, tokens }) => {
  const count = Number(value || 0);
  const disabled = count <= 0 || !projectId;

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onNavigate?.(buildProjectWorkspaceUrl({ projectId, rfqType }))}
      className={`text-[13px] font-bold ${disabled ? "cursor-default" : "underline underline-offset-2"}`}
      style={{ color: disabled ? tokens.faint : tokens.primary }}
    >
      {count}
    </button>
  );
};

const formatShare = (value = 0, total = 0) => {
  if (!total) return "0% of RFQs";
  return `${Math.round((Number(value || 0) / Number(total || 0)) * 100)}% of RFQs`;
};

const CountCell = ({ value, total, projectId, rfqType, onNavigate, showShare = false, tokens }) => (
  <div className="text-center">
    <CountLink value={value} projectId={projectId} rfqType={rfqType} onNavigate={onNavigate} tokens={tokens} />
    {showShare && (
      <p className="mt-0.5 text-[10px] font-semibold" style={{ color: tokens.faint }}>
        {formatShare(value, total)}
      </p>
    )}
  </div>
);

const mapProjectRows = (projects = []) =>
  projects.map((project) => ({
    projectId: project.projectId || "",
    name: project.projectName || "Unassigned project",
    totalRfqs: Number(project.totalRfqs || 0),
    materialRfqs: Number(project.materialRfqs || 0),
    contractorRfqs: Number(project.contractorRfqs || 0),
    labourRfqs: Number(project.labourRfqs || 0),
    closingAmount: Number(project.closingAmount || 0),
    savings: Number(project.savings || 0),
  }));

const buildTotalRow = (rows = []) =>
  rows.reduce(
    (acc, row) => ({
      totalRfqs: acc.totalRfqs + row.totalRfqs,
      materialRfqs: acc.materialRfqs + row.materialRfqs,
      contractorRfqs: acc.contractorRfqs + row.contractorRfqs,
      labourRfqs: acc.labourRfqs + row.labourRfqs,
      closingAmount: acc.closingAmount + row.closingAmount,
      savings: acc.savings + row.savings,
    }),
    {
      totalRfqs: 0,
      materialRfqs: 0,
      contractorRfqs: 0,
      labourRfqs: 0,
      closingAmount: 0,
      savings: 0,
    },
  );

const ProjectsSpendSummary = ({ projects = [], onNavigate, tokens }) => {
  const rows = mapProjectRows(projects);
  const summary = buildTotalRow(rows);

  return (
    <section className="mt-8">
      <div className="mb-3 flex items-center gap-3">
        <h2 className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-widest" style={{ color: tokens.faint }}>
          Project-wise spend summary
        </h2>
        <div className="h-px flex-1" style={{ backgroundColor: tokens.line }} />
      </div>

      <div
        className="overflow-hidden rounded-xl border shadow-[0_1px_3px_rgba(0,0,0,.06),0_1px_2px_rgba(0,0,0,.04)]"
        style={{ borderColor: tokens.line, backgroundColor: tokens.card }}
      >
        <div
          className="grid grid-cols-[54px_1.8fr_0.7fr_0.75fr_0.85fr_0.75fr_1fr_0.9fr] items-center border-b px-5 py-3 text-[11px] font-semibold uppercase tracking-wide"
          style={{ borderColor: tokens.line, color: tokens.faint }}
        >
          <p>Sr No</p>
          <p>Project</p>
          <p className="text-center">Total RFQs</p>
          <p className="text-center">Material</p>
          <p className="text-center">Contractor</p>
          <p className="text-center">Labour</p>
          <p className="text-right">Closing amount</p>
          <p className="text-right">Savings</p>
        </div>

        <div className="dashboard-print-expand-scroll max-h-[360px] overflow-y-auto">
          {!rows.length ? (
            <div className="px-5 py-8 text-center text-[13px] font-medium" style={{ color: tokens.faint }}>
              No project-wise RFQ data available.
            </div>
          ) : (
            rows.map((row, index) => (
              <div
                key={`${row.projectId}-${row.name}`}
                className="grid grid-cols-[54px_1.8fr_0.7fr_0.75fr_0.85fr_0.75fr_1fr_0.9fr] items-center border-b px-5 py-3"
                style={{ borderColor: tokens.line }}
              >
                <p className="text-[12px] font-semibold" style={{ color: tokens.faint }}>{index + 1}</p>
                <div className="min-w-0">
                  <p className="max-w-[260px] truncate text-[13px] font-semibold leading-tight" style={{ color: tokens.ink }}>{row.name}</p>
                </div>
                <CountCell value={row.totalRfqs} projectId={row.projectId} onNavigate={onNavigate} tokens={tokens} />
                <CountCell value={row.materialRfqs} total={row.totalRfqs} projectId={row.projectId} rfqType="material" onNavigate={onNavigate} showShare tokens={tokens} />
                <CountCell value={row.contractorRfqs} total={row.totalRfqs} projectId={row.projectId} rfqType="contractor" onNavigate={onNavigate} showShare tokens={tokens} />
                <CountCell value={row.labourRfqs} total={row.totalRfqs} projectId={row.projectId} rfqType="labour" onNavigate={onNavigate} showShare tokens={tokens} />
                <p className="text-right text-[13px] font-semibold" style={{ color: tokens.ink }}>{formatCurrency(row.closingAmount)}</p>
                <p className={`text-right text-[13px] font-bold ${row.savings < 0 ? "text-[#A32D2D]" : "text-[#127A2B]"}`}>
                  {formatCurrency(row.savings)}
                </p>
              </div>
            ))
          )}
        </div>

        <div className="grid grid-cols-[54px_1.8fr_0.7fr_0.75fr_0.85fr_0.75fr_1fr_0.9fr] items-center px-5 py-3 text-[13px] font-bold" style={{ backgroundColor: tokens.cardSoft, color: tokens.ink }}>
          <p />
          <p style={{ color: tokens.faint }}>Total</p>
          <p className="text-center">{summary.totalRfqs}</p>
          <p className="text-center">{summary.materialRfqs}</p>
          <p className="text-center">{summary.contractorRfqs}</p>
          <p className="text-center">{summary.labourRfqs}</p>
          <p className="text-right">{formatCurrency(summary.closingAmount)}</p>
          <p className={`text-right ${summary.savings < 0 ? "text-[#A32D2D]" : "text-[#127A2B]"}`}>
            {formatCurrency(summary.savings)}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSpendSummary;
